/**
 * AssignQRDialog Component
 * 
 * A dialog for scanning and assigning a QR code to a specific box.
 * Shows contextual UI based on whether the box already has a QR code assigned.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Divider,
  IconButton,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import QRScanner from './QRScanner';
import api from '../services/api';
import { colors, shadows } from '../styles/styleConstants';

const AssignQRDialog = ({ open, onClose, boxId, boxName, currentUuid, onSuccess }) => {
  const [step, setStep] = useState('choose'); // 'choose', 'scan', 'confirm', 'success'
  const [scannedUuid, setScannedUuid] = useState(null);
  const [uuidStatus, setUuidStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [manualUuid, setManualUuid] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [isGeneratingNew, setIsGeneratingNew] = useState(false); // Track if we're generating vs scanning

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep('choose');
      setScannedUuid(null);
      setUuidStatus(null);
      setError(null);
      setManualUuid('');
      setShowManualEntry(false);
      setIsGeneratingNew(false);
    }
  }, [open]);

  const handleScan = async ({ uuid }) => {
    setScannedUuid(uuid);
    setError(null);
    await checkUuidStatus(uuid);
  };

  const checkUuidStatus = async (uuid) => {
    setLoading(true);
    try {
      const response = await api.get(`/boxes/uuid/${uuid}/check`);
      setUuidStatus(response.data.status);
      
      if (response.data.status === 'taken') {
        setError('This QR code is already assigned to another user\'s box. Please scan a different code.');
        setStep('scan');
      } else if (response.data.status === 'owned' && response.data.box?.id !== boxId) {
        setError(`This QR code is already assigned to your box "${response.data.box?.name}". Please scan a different code.`);
        setStep('scan');
      } else {
        // Available or already assigned to this box
        setStep('confirm');
      }
    } catch (err) {
      console.error('Error checking UUID:', err);
      setError('Failed to check QR code status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualEntry = async () => {
    const uuid = manualUuid.trim().toLowerCase();
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    
    if (!uuidPattern.test(uuid)) {
      setError('Invalid UUID format. Please enter a valid UUID.');
      return;
    }
    
    setScannedUuid(uuid);
    await checkUuidStatus(uuid);
  };

  const handleAssign = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post(`/boxes/${boxId}/assign-uuid`, { uuid: scannedUuid });
      setStep('success');
      
      // Notify parent of success
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error assigning UUID:', err);
      if (err.response?.status === 409) {
        setError('This QR code is already assigned to another box.');
      } else {
        setError(err.response?.data?.error || 'Failed to assign QR code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNew = async () => {
    setLoading(true);
    setError(null);
    setIsGeneratingNew(true);
    
    try {
      const response = await api.post(`/boxes/${boxId}/regenerate-uuid`);
      setScannedUuid(response.data.uuid);
      setStep('success');
      
      // Notify parent of success
      if (onSuccess) {
        onSuccess(response.data);
      }
    } catch (err) {
      console.error('Error generating new UUID:', err);
      setError(err.response?.data?.error || 'Failed to generate new QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading && step !== 'scan') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Processing...</Typography>
        </Box>
      );
    }

    switch (step) {
      case 'choose':
        return (
          <Box>
            {currentUuid && (
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  This box already has a QR code assigned.
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 1 }}>
                  Current: {currentUuid}
                </Typography>
              </Alert>
            )}
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              Choose how you want to {currentUuid ? 'update' : 'add'} the QR code for this box:
            </Typography>
            
            {/* Option 1: Generate New QR */}
            <Paper
              sx={{
                p: 2.5,
                mb: 2,
                border: '2px solid',
                borderColor: colors.actionBlue,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: shadows.md,
                  backgroundColor: 'rgba(59, 130, 246, 0.04)'
                }
              }}
              onClick={handleGenerateNew}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AutorenewIcon sx={{ fontSize: 40, color: colors.actionBlue }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
                    Generate New QR Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a new QR code that you can print and attach to this box
                  </Typography>
                </Box>
              </Box>
            </Paper>
            
            {/* Option 2: Scan Existing QR */}
            <Paper
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: shadows.md,
                  borderColor: colors.mediumSlateBlue
                }
              }}
              onClick={() => setStep('scan')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QrCodeScannerIcon sx={{ fontSize: 40, color: colors.mediumSlateBlue }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
                    Scan Existing QR Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use a pre-printed QR code label that you already have
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        );
      
      case 'scan':
        return (
          <Box>
            <Button
              variant="text"
              onClick={() => setStep('choose')}
              sx={{ mb: 2 }}
            >
              ← Back to options
            </Button>
            
            {currentUuid && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Scanning a new code will replace the current QR code.
                </Typography>
              </Alert>
            )}
            
            {!showManualEntry ? (
              <>
                <QRScanner
                  onScan={handleScan}
                  onError={(err) => setError(err.message)}
                />
                
                <Divider sx={{ my: 2 }}>
                  <Typography variant="body2" color="text.secondary">OR</Typography>
                </Divider>
                
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => setShowManualEntry(true)}
                >
                  Enter UUID Manually
                </Button>
              </>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Enter the UUID from your ClutterLogix QR code label. The UUID is printed below the QR code.
                </Typography>
                <TextField
                  fullWidth
                  label="UUID from ClutterLogix Label"
                  value={manualUuid}
                  onChange={(e) => setManualUuid(e.target.value)}
                  placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890"
                  helperText="Only UUIDs from ClutterLogix QR codes are accepted"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowManualEntry(false);
                      setManualUuid('');
                    }}
                  >
                    Back to Scanner
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleManualEntry}
                    disabled={!manualUuid.trim()}
                    sx={{ backgroundColor: colors.actionBlue }}
                  >
                    Check UUID
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        );
      
      case 'confirm':
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <QrCodeScannerIcon sx={{ fontSize: 48, color: colors.actionBlue, mb: 2 }} />
            
            <Typography variant="h6" sx={{ mb: 2 }}>
              {currentUuid ? 'Replace QR Code?' : 'Assign QR Code?'}
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
              <Typography variant="body2" color="text.secondary">
                Box: <strong>{boxName}</strong>
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 1 }}>
                New UUID: {scannedUuid}
              </Typography>
              {currentUuid && (
                <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5, color: 'warning.main' }}>
                  Replacing: {currentUuid}
                </Typography>
              )}
            </Paper>
            
            {currentUuid && (
              <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                The old QR code will no longer work for this box.
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setStep('scan');
                  setScannedUuid(null);
                }}
              >
                Scan Different Code
              </Button>
              <Button
                variant="contained"
                onClick={handleAssign}
                disabled={loading}
                sx={{ backgroundColor: colors.actionBlue }}
              >
                {loading ? <CircularProgress size={24} /> : (currentUuid ? 'Replace' : 'Assign')}
              </Button>
            </Box>
          </Box>
        );
      
      case 'success':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {isGeneratingNew ? 'New QR Code Generated!' : (currentUuid ? 'QR Code Replaced!' : 'QR Code Assigned!')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isGeneratingNew 
                ? `A new QR code has been generated for "${boxName}".`
                : `The QR code has been successfully ${currentUuid ? 'replaced' : 'assigned'} to "${boxName}".`
              }
            </Typography>
            {scannedUuid && (
              <Paper sx={{ p: 1.5, mb: 3, bgcolor: 'grey.100', display: 'inline-block' }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {scannedUuid}
                </Typography>
              </Paper>
            )}
            {isGeneratingNew && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  You can now print this QR code using the "QR Label" button.
                </Typography>
              </Alert>
            )}
            <Box>
              <Button variant="contained" onClick={onClose} sx={{ backgroundColor: colors.actionBlue }}>
                Done
              </Button>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: shadows.xl
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon sx={{ color: colors.actionBlue }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.darkSlateBlue }}>
            {currentUuid ? 'Manage QR Code' : 'Add QR Code'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default AssignQRDialog;
